import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

const TRANS_PATH = 'ADD_BANK_ACCOUNT_FORM.FORM_SCHEMA';

const buildBankAccountAddressSchema = (t: TFunction<'common'>) =>
  z.object({
    street: z.string().min(1, {
      message: t(`${TRANS_PATH}.STREET_REQUIRED`),
    }),
    addition: z.string(),
    zip: z.string().min(1, { message: t(`${TRANS_PATH}.ZIP_REQUIRED`) }),
    city: z.string().min(1, { message: t(`${TRANS_PATH}.CITY_REQUIRED`) }),
    cityEnglish: z.string(),
    state: z.string(),
    country: z.string().min(1, {
      message: t(`${TRANS_PATH}.COUNTRY_REQUIRED`),
    }),
  });

const buildBankAccountFormSchema = (t: TFunction<'common'>) =>
  z.object({
    accountHolderName: z.string().min(1, {
      message: t(`${TRANS_PATH}.ACCOUNT_HOLDER_NAME_REQUIRED`),
    }),
    accountHolderAddress: buildBankAccountAddressSchema(t),
    currency: z.string().min(1, {
      message: t(`${TRANS_PATH}.CURRENCY_REQUIRED`),
    }),
    bankName: z.string().min(1, {
      message: t(`${TRANS_PATH}.BANK_NAME_REQUIRED`),
    }),
    bankAddress: buildBankAccountAddressSchema(t),
    accountNumber: z.string(),
    bicSwift: z.string(),
    iban: z.string(),
    routingNumber: z.string(),
    notes: z.string(),
    creditorNumber: z.string(),
  });

export type BankAccountFormSchemaType = z.infer<
  ReturnType<typeof buildBankAccountFormSchema>
>;

const emptyAddress: BankAccountFormSchemaType['accountHolderAddress'] = {
  street: '',
  addition: '',
  zip: '',
  city: '',
  cityEnglish: '',
  state: '',
  country: '',
};

const defaultValues: BankAccountFormSchemaType = {
  accountHolderName: '',
  accountHolderAddress: emptyAddress,
  currency: 'EUR',
  bankName: '',
  bankAddress: { ...emptyAddress },
  accountNumber: '',
  bicSwift: '',
  iban: '',
  routingNumber: '',
  notes: '',
  creditorNumber: '',
};

type UseBankAccountFormSchemaReturnType = {
  schema: ReturnType<typeof buildBankAccountFormSchema>;
  defaultValues: BankAccountFormSchemaType;
};

export const useBankAccountFormSchema =
  (): UseBankAccountFormSchemaReturnType => {
    const { t } = useTranslation('common');

    return {
      schema: buildBankAccountFormSchema(t),
      defaultValues,
    };
  };
